import React, { useMemo } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { tokens } from "../../Common/designTokens";
import { utcToLocal } from "../../Utils/timeConversions";

const toneStyles = {
  success: {
    chipBg: "rgba(34, 197, 94, 0.12)",
    chipBorder: "rgba(34, 197, 94, 0.35)",
    text: "#15803d",
  },
  warning: {
    chipBg: "rgba(245, 158, 11, 0.12)",
    chipBorder: "rgba(245, 158, 11, 0.35)",
    text: "#b45309",
  },
  error: {
    chipBg: "rgba(239, 68, 68, 0.12)",
    chipBorder: "rgba(239, 68, 68, 0.35)",
    text: "#b91c1c",
  },
};

const getFirstDisplayValue = (values = []) =>
  values.find((value) => value !== null && value !== undefined && `${value}`.trim() !== "") ?? "";

const formatModuleStatus = (value) => {
  const rawValue = `${value || ""}`.trim();
  if (!rawValue) return "";

  return rawValue
    .toLowerCase()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const normalizeModule = (module, index) => {
  if (typeof module === "string" || typeof module === "number") {
    return {
      name: String(module),
      status: "",
      details: "",
    };
  }

  if (module && typeof module === "object") {
    return {
      name:
        getFirstDisplayValue([
          module.displayName,
          module.moduleDisplayName,
          module.moduleName,
          module.name,
          module.code,
          module.id,
        ]) || `Module ${index + 1}`,
      status: formatModuleStatus(
        getFirstDisplayValue([
          module.moduleStatus,
          module.status,
          module.healthStatus,
          module.state,
        ])
      ),
      details: getFirstDisplayValue([
        module.message,
        module.reason,
        module.description,
        module.error,
      ]),
    };
  }

  return {
    name: `Module ${index + 1}`,
    status: "",
    details: "",
  };
};

const detailCardSx = {
  border: `1px solid ${tokens.colors.bodyBorderColor50}`,
  borderRadius: "12px",
  backgroundColor: tokens.colors.defaultGridRowColor,
  p: 2,
  display: "flex",
  flexDirection: "column",
  gap: 1,
};

const DAILY_PROCESS_TONE_MAP = {
  COMPLETED: "success",
  IN_PROGRESS: "warning",
  ERROR: "error",
  FAILED: "error",
  UNKNOWN: "warning",
};

const formatDailyProcessStatusLabel = (value) => {
  const rawValue = `${value || ""}`.trim();
  if (!rawValue) return "Unknown";

  return rawValue
    .toLowerCase()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatTimestamp = (value, fallback = "---") =>
  utcToLocal(value)?.format("D MMM YY, HH:mm:ss") || fallback;

const formatNumberValue = (value, fallback = "---") =>
  value === null || value === undefined || value === "" ? fallback : `${value}`;

const MarketDataHealthModal = ({
  open = false,
  onClose,
  marketDataHealth = {},
  dailyProcessStatus = {},
}) => {
  const statusTone = marketDataHealth?.tone || "warning";
  const activeTone = toneStyles[statusTone] || toneStyles.warning;
  const formattedTimestamp = formatTimestamp(marketDataHealth?.timestamp);

  const dailyProcessCode = String(dailyProcessStatus?.status || "").trim().toUpperCase() || "UNKNOWN";
  const dailyProcessToneKey = DAILY_PROCESS_TONE_MAP[dailyProcessCode] || "warning";
  const dailyProcessTone = toneStyles[dailyProcessToneKey] || toneStyles.warning;
  const dailyProcessStatusLabel = formatDailyProcessStatusLabel(dailyProcessCode);

  const normalizedModules = useMemo(() => {
    const modules = Array.isArray(marketDataHealth?.modules) ? marketDataHealth.modules : [];
    return modules.map(normalizeModule);
  }, [marketDataHealth?.modules]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          border: `1px solid ${tokens.colors.bodyBorderColor50}`,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`,
          backgroundColor: tokens.colors.bodyBackgroundColor,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: tokens.colors.fontColour }}>
              Market Data Health
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: "0.875rem", color: tokens.colors.fontColour85 }}>
              Admin visibility only
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: tokens.colors.fontColour }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          pt: '16px !important', // or use theme spacing equivalent
          pb: 3,
          backgroundColor: tokens.colors.bodyBackgroundColor,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={detailCardSx}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: tokens.colors.fontColour85 }}>
              STATUS
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Chip
                label={marketDataHealth?.statusLabel || "Unknown"}
                sx={{
                  fontWeight: 700,
                  color: activeTone.text,
                  backgroundColor: activeTone.chipBg,
                  border: `1px solid ${activeTone.chipBorder}`,
                  "& .MuiChip-label": {
                    px: 1.25,
                  },
                }}
              />
              <Typography sx={{ fontSize: "0.9rem", color: tokens.colors.fontColour85 }}>
                {marketDataHealth?.message || "Market data health is currently unavailable."}
              </Typography>
            </Box>
          </Box>

          <Box sx={detailCardSx}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: tokens.colors.fontColour85 }}>
              TIMESTAMP
            </Typography>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: tokens.colors.fontColour }}>
              {formattedTimestamp}
            </Typography>
          </Box>

          <Box sx={detailCardSx}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: tokens.colors.fontColour85 }}>
              MODULES
            </Typography>
            {normalizedModules.length === 0 ? (
              <Box
                sx={{
                  borderRadius: "10px",
                  border: `1px dashed ${tokens.colors.bodyBorderColor}`,
                  backgroundColor: tokens.colors.bodyBackgroundColor,
                  px: 1.5,
                  py: 2,
                }}
              >
                <Typography sx={{ fontSize: "0.9rem", color: tokens.colors.fontColour85 }}>
                  No impacted modules reported.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {normalizedModules.map((module, index) => (
                  <Box
                    key={`${module.name}-${index}`}
                    sx={{
                      borderRadius: "10px",
                      border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                      backgroundColor: tokens.colors.bodyBackgroundColor,
                      px: 1.5,
                      py: 1.25,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: tokens.colors.fontColour }}>
                        {module.name}
                      </Typography>
                      {module.status ? (
                        <Chip
                          size="small"
                          label={module.status}
                          sx={{
                            fontWeight: 700,
                            color: tokens.colors.fontColour85,
                            backgroundColor: tokens.colors.defaultGridRowColor,
                          }}
                        />
                      ) : null}
                    </Box>
                    {module.details ? (
                      <Typography sx={{ mt: 0.75, fontSize: "0.85rem", color: tokens.colors.fontColour85 }}>
                        {module.details}
                      </Typography>
                    ) : null}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Box sx={detailCardSx}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: tokens.colors.fontColour85 }}>
              DAILY WATCHLIST PROCESS
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Chip
                label={dailyProcessStatusLabel}
                sx={{
                  fontWeight: 700,
                  color: dailyProcessTone.text,
                  backgroundColor: dailyProcessTone.chipBg,
                  border: `1px solid ${dailyProcessTone.chipBorder}`,
                  "& .MuiChip-label": {
                    px: 1.25,
                  },
                }}
              />
              <Typography sx={{ fontSize: "0.9rem", color: tokens.colors.fontColour85 }}>
                {dailyProcessStatus?.message || "No daily process message reported."}
              </Typography>
            </Box>
            <Box
              sx={{
                mt: 1,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                gap: 1.25,
              }}
            >
              <Box>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: tokens.colors.fontColour85 }}>
                  STARTED
                </Typography>
                <Typography sx={{ fontSize: "0.9rem", color: tokens.colors.fontColour }}>
                  {formatTimestamp(dailyProcessStatus?.startTime)}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: tokens.colors.fontColour85 }}>
                  FINISHED
                </Typography>
                <Typography sx={{ fontSize: "0.9rem", color: tokens.colors.fontColour }}>
                  {formatTimestamp(dailyProcessStatus?.endTime)}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: tokens.colors.fontColour85 }}>
                  DURATION
                </Typography>
                <Typography sx={{ fontSize: "0.9rem", color: tokens.colors.fontColour }}>
                  {dailyProcessStatus?.durationSeconds != null
                    ? `${dailyProcessStatus.durationSeconds} seconds`
                    : "---"}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: tokens.colors.fontColour85 }}>
                  LAST CHECKED
                </Typography>
                <Typography sx={{ fontSize: "0.9rem", color: tokens.colors.fontColour }}>
                  {formatTimestamp(dailyProcessStatus?.checkedAt)}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: tokens.colors.fontColour85 }}>
                  PROCESSED
                </Typography>
                <Typography sx={{ fontSize: "0.9rem", color: tokens.colors.fontColour }}>
                  {formatNumberValue(dailyProcessStatus?.totalProcessed)}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: tokens.colors.fontColour85 }}>
                  INSERTED
                </Typography>
                <Typography sx={{ fontSize: "0.9rem", color: tokens.colors.fontColour }}>
                  {formatNumberValue(dailyProcessStatus?.totalInserted)}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: tokens.colors.fontColour85 }}>
                  DELETED
                </Typography>
                <Typography sx={{ fontSize: "0.9rem", color: tokens.colors.fontColour }}>
                  {formatNumberValue(dailyProcessStatus?.totalDeleted)}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: tokens.colors.fontColour85 }}>
                  ERRORS
                </Typography>
                <Typography sx={{ fontSize: "0.9rem", color: tokens.colors.fontColour }}>
                  {formatNumberValue(dailyProcessStatus?.totalErrors)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MarketDataHealthModal;
